import { motion } from "motion/react";
import type React from "react";
import { useRef, useState } from "react";
import { twJoin } from "tailwind-merge";

import { Spread } from "@/design-system/Spread";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import { RuntimeChartLeft, RuntimeChartTranslate } from "@/ui-adapters/RuntimeChartGeometry";
import { RuntimeWidth } from "@/ui-adapters/RuntimeWidth";

const WIDTH_INDICATOR = 4;
const VALUE_LABEL_CLASS_NAME =
  "h-5 rounded-md border border-border-default bg-background-default px-1 py-0.5 text-overline-emphasis leading-tight text-text-primary";

interface MeterChartProps {
  /** The primary value to display on the top bar (0-100). */
  primaryValue: number;
  /** The secondary value to display on the bottom bar (0-100). */
  secondaryValue: number;
  /** ARIA label for the chart container for accessibility. */
  ariaLabel: string;
}

export function MeterChart({ primaryValue, secondaryValue, ariaLabel }: MeterChartProps): React.ReactNode {
  const refChartContainer = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const refIndicatorValue = useRef<HTMLHeadingElement>(null);
  const [indicatorValueWidth, setIndicatorValueWidth] = useState(0);

  useResizeObserver(refIndicatorValue, (entries) => {
    for (const entry of entries) {
      if (entry.contentRect) {
        setIndicatorValueWidth(entry.contentRect.width);
      }
    }
  });

  useResizeObserver(refChartContainer, (entries) => {
    for (const entry of entries) {
      if (entry.contentRect) {
        setChartWidth(entry.contentRect.width);
      }
    }
  });

  // Calculate indicator position
  const availableChartWidth = chartWidth - WIDTH_INDICATOR;
  const primaryValueIndicatorPosition = (primaryValue / 100) * availableChartWidth;
  // Calculate left and right compensation for the indicator to prevent overflow at extremes
  const isLeftSideEnough = primaryValueIndicatorPosition >= indicatorValueWidth / 2;
  const isRightSideEnough = primaryValueIndicatorPosition + indicatorValueWidth / 2 <= availableChartWidth;
  const leftSideCompensationWidth = isLeftSideEnough ? 0 : indicatorValueWidth / 2 - primaryValueIndicatorPosition;
  const rightSideCompensationWidth = isRightSideEnough
    ? 0
    : primaryValueIndicatorPosition + indicatorValueWidth / 2 - availableChartWidth;

  return (
    <figure className="my-space-sm flex h-meter-frame w-full flex-col justify-end gap-space-sm" aria-label={ariaLabel}>
      <div ref={refChartContainer} className="relative flex w-full flex-col">
        {/* Primary Bar with Indicator */}
        <div className="relative box-content h-meter-indicator w-full pt-space-lg">
          {/* Background bar */}
          <div className="absolute bottom-0 h-meter-bar w-full bg-background-success" />

          {/* Animated Mask to reveal primary */}
          <RuntimeWidth width={chartWidth - primaryValueIndicatorPosition}>
            {(maskAttributes) => (
              <motion.div
                {...maskAttributes}
                variants={{ initial: { x: 0 }, animate: { x: primaryValueIndicatorPosition } }}
                initial="initial"
                animate={chartWidth > 0 ? "animate" : "initial"}
                transition={{ duration: 0.8, ease: [0.4, 0, 0, 0.99], delay: 0.2 }}
                className="absolute bottom-0 z-10 h-meter-bar bg-background-default"
              >
                {/* Primary Value Indicator */}
                <RuntimeWidth width={WIDTH_INDICATOR}>
                  {(indicatorAttributes) => (
                    <div
                      {...indicatorAttributes}
                      className="absolute -bottom-px z-20 flex flex-col items-center gap-space-xs"
                    >
                      <RuntimeChartTranslate offset={leftSideCompensationWidth - rightSideCompensationWidth}>
                        {(labelAttributes) => (
                          <motion.h4
                            {...labelAttributes}
                            ref={refIndicatorValue}
                            variants={{ initial: { opacity: 0 }, animate: { opacity: 1 } }}
                            initial="initial"
                            animate={chartWidth > 0 ? "animate" : "initial"}
                            transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                            className={VALUE_LABEL_CLASS_NAME}
                          >
                            {Math.round(primaryValue)}%
                          </motion.h4>
                        )}
                      </RuntimeChartTranslate>
                      <RuntimeWidth width={WIDTH_INDICATOR}>
                        {(lineAttributes) => (
                          <span
                            {...lineAttributes}
                            className="relative bottom-px h-meter-indicator bg-background-brand-secondary"
                          />
                        )}
                      </RuntimeWidth>
                    </div>
                  )}
                </RuntimeWidth>
              </motion.div>
            )}
          </RuntimeWidth>
        </div>

        {/* Secondary Bar with Animation */}
        <div className="relative h-meter-bar w-full overflow-hidden bg-background-default">
          <motion.div
            variants={{
              initial: {
                width: 0,
              },
              animate: {
                width: `${secondaryValue}%`,
              },
            }}
            initial="initial"
            animate={chartWidth > 0 ? "animate" : "initial"}
            transition={{ duration: 0.8, ease: [0.4, 0, 0, 0.99], delay: 0.3 }}
            className="h-full bg-background-brand-secondary"
          />

          {/* Secondary Value Label */}
          <RuntimeChartLeft
            left={secondaryValue > 10 ? `calc(${secondaryValue}% - 4px)` : `calc(${secondaryValue}% + 4px)`}
          >
            {(positionAttributes) => (
              <motion.div
                {...positionAttributes}
                variants={{
                  initial: {
                    opacity: 0,
                  },
                  animate: {
                    opacity: 1,
                  },
                }}
                initial="initial"
                animate={chartWidth > 0 ? "animate" : "initial"}
                transition={{ duration: 0.6, delay: 0.5, ease: "easeOut" }}
                className={twJoin(
                  "absolute top-0.5 bottom-0",
                  VALUE_LABEL_CLASS_NAME,
                  secondaryValue > 10 && "-translate-x-full",
                )}
              >
                {Math.round(secondaryValue)}%
              </motion.div>
            )}
          </RuntimeChartLeft>
        </div>

        {/* Bottom Axis Line */}
        <div className="absolute inset-x-0 -bottom-0.5 h-0.5 bg-background-tertiary" />
      </div>

      {/* 0-100 Labels */}
      <Spread>
        <span aria-label="0%" className="text-overline text-text-secondary">
          0
        </span>
        <span aria-label="100%" className="text-overline text-text-secondary">
          100
        </span>
      </Spread>
    </figure>
  );
}
